import { Component, Input } from "@angular/core";
import { UserPermissionType } from "@apiserver-rest/ponder-auth-client-base";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DataResponse, GetMagicSetsResponse, MagicCard, MagicSet, UpdateMagicCardChanges, UpdateMagicCardRequest } from "@apiserver-rest/ponder-data-client-base";
import { AuthService } from "@ponder-core/auth/auth.service";
import { RoleAndPermissions } from "@ponder-core/auth/role-permissions";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { take } from "rxjs";
import { getMagicCardRoute } from "./magicCardRouter";
import { NavigationService } from "@ponder-core/navigation/navigation.service";

@Component({
    selector: "magic-card-admin",
    imports: [LoadingComponent, BusySpinComponent],
    templateUrl: "./magic-card-admin.component.html"
})
export class MagicCardAdminComponent {
    @Input({ required: true }) public magicCard!: MagicCard;

    public canView: boolean = false;
    public isOpen: boolean = false;

    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;

    public isUpdating: boolean = false;
    public updatingSuccess: boolean = true;
    
    private allMagicSets: MagicSet[] = [];
    public visibleMagicSets: MagicSet[] = [];

    public constructor(private ponderDataClient: PonderDataClient, private authService: AuthService, private navigation: NavigationService) {

    }

    public ngOnInit(): void {
        this.authService.getRoleAndPermissions().then((roleAndPermissions: RoleAndPermissions | undefined) => {
            if (roleAndPermissions) {
                this.canView = roleAndPermissions.hasPermission(UserPermissionType.EditMagic);
            }
        });
    }

    public clickedOpen(): void {
        if (this.isOpen || !this.canView) {
            return;
        }

        this.isOpen = true;
        this.fetchData();
    }

    private fetchData(): void {
        this.ponderDataClient
            .magicSetAll()
            .pipe(take(1))
            .subscribe({
                next: (response: GetMagicSetsResponse) => {
                    if (response.sets) {
                        this.allMagicSets = response.sets;
                        this.allMagicSets.sort((a, b) => {
                            var aTime = a.releaseDate.getTime();
                            var bTime = b.releaseDate.getTime();

                            if (aTime != bTime) {
                                return (bTime - aTime);                     // Newest first
                            } else {
                                return -a.code!.localeCompare(b.code!);     // Reverse alphabetical
                            }                                               // This is done so newest SLD is first as that is most likely why we are here.
                        });

                        this.visibleMagicSets = this.allMagicSets;
                        this.loadingSuccess = true;
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    public onMagicSetFilterChange(value: string, moveToSetCheckbox: HTMLInputElement): void {
        value = value.toLowerCase().trim();

        if (value.length < 3) {
            this.visibleMagicSets = this.allMagicSets;
        }

        this.visibleMagicSets = this.allMagicSets.filter((set: MagicSet, index: number, array: MagicSet[]) => {
            return (set.name!.toLowerCase().indexOf(value) >= 0) || (set.code!.indexOf(value) >= 0);
        });

        if (this.visibleMagicSets) {
            moveToSetCheckbox.checked = true;
        }
    }

    public onUpdateClicked(refreshImageCheckbox: HTMLInputElement, moveToSetCheckbox: HTMLInputElement, moveToSetSelect: HTMLSelectElement) {
        if (this.isUpdating) {
            return;
        }

        this.isUpdating = true;
        this.updatingSuccess = true;

        var refreshImage = refreshImageCheckbox.checked;
        var moveToSet = moveToSetCheckbox.checked;
        var setCode = (moveToSet ? moveToSetSelect.value : undefined);

        var request = new UpdateMagicCardRequest({
            magicCardId: this.magicCard.id!,
            changes: new UpdateMagicCardChanges({
                updateImage: refreshImage,
                moveToSet: setCode
            })
        });

        this.ponderDataClient
            .magicCardUpdate(request)
            .pipe(take(1))
            .subscribe({
                next: (response: DataResponse) => {
                    this.updatingSuccess = response.success ?? false;

                    if (response.success && moveToSet) {
                        this.navigation.goto(`/magic/card/${setCode}/${this.magicCard.setIndex}`);     // Route to new home of the card
                    }
                },
                error: () => {
                    this.updatingSuccess = false;
                }
            })
            .add(() => { this.isUpdating = false; });
    }
}